


import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../../Redux/Slices/AuthSlice";
import { decryptToken } from "../../../Utlis/Encryption.js";
import { BASE_URL } from "../../../components/BaseUrl.js";
import CustomText, { textStyles } from "../../../components/CustomText";
import GroupIcon from "@mui/icons-material/Group";
import EventIcon from "@mui/icons-material/Event";
import CheckIcon from "@mui/icons-material/Check";
import GlobalBannerAlert from "../../../components/GlobalPaymentAlert.js";
import CustomButton from "../../../components/CustomButton.js";
import { Modal, IconButton, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

const CurrentPlans = () => {
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = useSelector((state) => state.auth.token);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const upgradeToken = new URLSearchParams(window.location.search).get("upgrade_token");
  const decryptedToken = upgradeToken || (token ? decryptToken(token) : null);
const [showBanner, setShowBanner] = useState(true);
const [openModal, setOpenModal] = useState(false);
const [quantity, setQuantity] = useState(0);
const [initialQuantity, setInitialQuantity] = useState(0);
const [calculating, setCalculating] = useState(false);
const [updating, setUpdating] = useState(false);
const [pricingResult, setPricingResult] = useState(null);
const [topupQuote, setTopupQuote] = useState(null);
const [showTopupModal, setShowTopupModal] = useState(false);
const { t } = useTranslation();
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/payments/company-admin/plan-details`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${decryptedToken}`,
            },
          }
        );

        if (response.status === 401) {
          // No valid session (e.g. upgrade link already used/expired, or
          // stale/missing token) — nothing to show here, send to login.
          dispatch(logout());
          navigate("/", { replace: true });
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setPlanData(result.data);
      } catch (error) {
        console.error("Error fetching plan details:", error);
        setPlanData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decryptedToken]);

  const handleCalculate = useCallback(async (planId, qty, silent = false) => {
  try {
  if (!decryptedToken) {
      toast.error(t("alerts.auth_missing"));
      return;
    }
    setCalculating(true);
    if (!silent) setPricingResult(null);

    const res = await fetch(`${BASE_URL}/payments/calculate-price`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${decryptedToken}`,
      },
      body: JSON.stringify({
        plan_id: planId,
        worker_count: String(qty),
      }),
    });

    const json = await res.json();
    if (!res.ok || json.error) throw new Error(json.message || "Error calculating price");
    console.log("Calculating price for:", { planId, qty });
    if (!silent) toast.success(t("alerts.calculate_success"));
    setPricingResult(json.data);
  } catch (err) {
    console.error(err);
    toast.error(err.message || t("alerts.calculate_error"));
  } finally {
    setCalculating(false);
  }
}, [decryptedToken, t]);

  useEffect(() => {
    if (!openModal || !planData?.current_plan?.plan_id || quantity <= 0) return;

    const timer = setTimeout(() => {
      handleCalculate(planData.current_plan.plan_id, quantity, true);
    }, 400);

    return () => clearTimeout(timer);
  }, [openModal, planData?.current_plan?.plan_id, quantity, handleCalculate]);


const handleContinue = async () => {
  try {
   if (!decryptedToken) {
      toast.error(t("alerts.auth_missing"));
      return;
    }
     if (!pricingResult) {
      toast.error(t("alerts.calculate_first"));
      return;
    }

    setUpdating(true);

    const payload = { new_worker_count: String(quantity) };
    console.log("Update worker count API request:", payload);

    const res = await fetch(`${BASE_URL}/payments/company-admin/update-worker-count`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${decryptedToken}`,
      },
      body: JSON.stringify(payload),
    });

    const json = await res.json();
    console.log("Update worker count API response status:", res.status);
    console.log("Update worker count API response:", json);
    console.log("Update worker count API response data:", json.data);

    if (!res.ok || json.error) {
      throw new Error(json.message || t("alerts.update_error"));
    }

    // Worker top-up: requires an immediate prorated PayPal payment.
    // Show a confirmation step before redirecting to PayPal.
    if (json?.data?.topup_quote && json?.data?.paypal_order?.approval_url) {
      setTopupQuote({
        ...json.data.topup_quote,
        approvalUrl: json.data.paypal_order.approval_url,
      });
      setShowTopupModal(true);
      setOpenModal(false);
      return;
    }

    toast.success( t("alerts.update_success"));

    // Save response data to localStorage so we can use it later
    localStorage.setItem("revisionData", JSON.stringify(json.data));

    // Navigate to PayPal approval page
    const approvalUrl = json?.data?.paypal_revision?.approval_url;
    if (approvalUrl) {
      window.location.href = approvalUrl;
    } else {
      toast.error(t("alerts.no_approval_url"));
    }

  } catch (err) {
    console.error(err);
    toast.error(err.message || t("alerts.update_error"));
  } finally {
    setUpdating(false);
  }
};

const handleConfirmTopupPayment = () => {
  if (topupQuote?.approvalUrl) {
    window.location.href = topupQuote.approvalUrl;
  } else {
    toast.error(t("alerts.no_approval_url"));
  }
};

  const [changingPayment, setChangingPayment] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const handleChangePaymentMethod = async () => {
    try {
       if (!decryptedToken) {
      toast.error(t("alerts.auth_missing"));
      return;
    }

      setChangingPayment(true);
      setPaymentInfo(null);

      const upgradeQuery = upgradeToken
        ? `?upgrade_token=${encodeURIComponent(upgradeToken)}`
        : "";
      const res = await fetch(`${BASE_URL}/payments/company-admin/update-payment-method${upgradeQuery}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${decryptedToken}`,
        },
      });

      const json = await res.json();
      console.log("🔄 Payment Method Update Response:", json);
      setChangingPayment(false);

      if (!res.ok || json.error) {
      throw new Error(json.message || t("alerts.payment_update_error"));
      }

    toast.success( t("alerts.payment_update_success"));
      setPaymentInfo(json.data);

      // If approval required, redirect user to PayPal approval_url
      const approvalUrl = json?.data?.payment_method_update?.approval_url;
      const requiresApproval = json?.data?.payment_method_update?.requires_approval;

      if (requiresApproval && approvalUrl) {
        console.log("➡️ Redirecting to:", approvalUrl);
        window.location.href = approvalUrl;
      }
    } catch (err) {
      setChangingPayment(false);
      console.error("❌ Update Payment Method Error:", err);
    toast.error(err.message || t("alerts.general_error"));
    }
  };



  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!planData) {
    return (
      <Typography
        sx={{
          fontSize: "13px",
          fontFamily: "Poppins, sans-serif",
          color: "red",
        }}
      >
        Failed to load plan details.
      </Typography>
    );
  }
const { current_plan, subscription_status } = planData;
// ===== Trial Worker Count Override =====
const isTrial = subscription_status?.status === "trial";

const displayedWorkerCount = isTrial
  ? 5
  : planData?.pricing_info?.current_pricing_breakdown?.worker_count || 0;
const isFreePlan = current_plan?.plan_id === "free" || current_plan?.key === "free";
const isYearlyPlan = current_plan?.billing_cycle === "yearly";
  return (
    <>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{
          display: "flex", justifyContent: "space-between", alignItems: "center"
        }}>


      <CustomText
        sx={{
          ...textStyles.h1,
          fontWeight: 500,
          fontSize: "16px",
          color: "#003149",
          textAlign: "left",
        }}
      >
   {t("subscriptionPage.current_subscription_plan")}
      </CustomText>
      <Box sx={{  display: "flex",gap:2,flexWrap:"wrap", alignItems: "center"}}>
 {!upgradeToken && !isYearlyPlan && (
 <CustomButton
  label={t("subscriptionPage.add_workers")}
  size="small"
  onClick={() => {
    // const currentWorkers = planData?.pricing_info?.current_pricing_breakdown?.worker_count || 0;
    const currentWorkers = displayedWorkerCount; // trial = 5

    setQuantity(currentWorkers);
    setInitialQuantity(currentWorkers);
    setPricingResult(null);
    setOpenModal(true);
  }}
/>
 )}
 {!upgradeToken && (
  <CustomButton
        size="small"
        disabled={changingPayment}
        onClick={handleChangePaymentMethod}
        label={
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            {changingPayment && (
              <CircularProgress
                size={16}
                sx={{ color: "#fff", mr: 1 }}
              />
            )}
        {changingPayment ? t("subscriptionPage.updating") : t("subscriptionPage.change_payment_method")}
          </Box>
        }
      />
 )}
</Box>
        </Box>
      {/* Current Plan Box */}
      <Box
        sx={{
          border: "1px solid #ddd",
          borderRadius: 2,
          p: 2,
        }}
      >
     

          {/* Plan name with status */}
<Box
  sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  }}
>
  <CustomText
    sx={{
      ...textStyles.h1,
      fontWeight: 600,
      fontSize: "18px",
      color: "#003149",
    }}
  >
    {current_plan?.name || "No Plan Name"}
  </CustomText>


{!isFreePlan && planData?.subscription_status && (
  <Box
    sx={{
      backgroundColor:
        planData.subscription_status.status === "trial" ||
        planData.subscription_status.status === "subscription error"
          ? "#f0ad4e"
          : planData.subscription_status.status === "cancelled"
          ? "#d32f2f"
          : "#5cb85c",
      color: "#fff",
      fontSize: "12px",
      fontFamily: "Poppins, sans-serif",
      fontWeight: 500,
      px: 1.5,
      py: 0.4,
      borderRadius: "4px",
      textTransform: "capitalize",
    }}
  >
    {planData.subscription_status.status === "trial" ||
    planData.subscription_status.status === "subscription error"
      ? "Trial"
      : planData.subscription_status.status === "cancelled"
      ? "Cancelled"
      : planData.subscription_status.detailed_status?.replace("_", " ")}
  </Box>
)}

</Box>




        {planData?.pricing_info?.current_pricing_breakdown && (
  <Box
    sx={{
      border: "1px solid #ddd",
      borderRadius: 2,
      p: 2,
      mt: 2,
      // backgroundColor: "#fafafa",
    }}
  >    <Typography
      sx={{
        fontSize: "14px",
        fontWeight: 600,
        fontFamily: "Poppins, sans-serif",
        mb: 1,
        color: "#003149",
      }}
    >
       {t("subscriptionPage.pricing_details")}
    </Typography>
    {[
      { label:  t("subscriptionPage.worker_count"), value: 
        // planData.pricing_info.current_pricing_breakdown.worker_count
        displayedWorkerCount
       },
      { label: t("subscriptionPage.base_amount"), value: `$${planData.pricing_info.current_pricing_breakdown.base_amount}` },
      { label: t("subscriptionPage.per_worker_amount"), value: `$${planData.pricing_info.current_pricing_breakdown.per_worker_amount}` },
      { label: t("subscriptionPage.additional_worker_cost"), value: `$${planData.pricing_info.current_pricing_breakdown.additional_worker_cost}` },
      { label: t("subscriptionPage.total_amount"), value: `$${planData.pricing_info.current_pricing_breakdown.total_amount}` },
    ].map((item, index) => (
      <Box
        key={index}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 0.6,
          borderBottom: index !== 4 ? "1px dashed #eee" : "none",
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontFamily: "Poppins, sans-serif",
            color: "#333",
          }}
        >
          {item.label}
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            fontFamily: "Poppins, sans-serif",
            color: "#000",
          }}
        >
          {item.value}
        </Typography>
      </Box>
    ))}
  </Box>
)}

{subscription_status?.status === "trial" && planData?.trial_info && (
  <Box
    sx={{
      // border: "1px solid #ddd",
      borderRadius: 2,
      // p: 2,
      mt:1,
      // backgroundColor: "#fafafa",
    
    }}
  >
    <Box sx={{
        display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      textAlign: "center",
    }}>
      <Typography
        sx={{
          fontSize: "13px",
          fontFamily: "Poppins, sans-serif",
          color: "#333",
        }}
      >
      {t("subscriptionPage.trial_days_remaining")}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          fontFamily: "Poppins, sans-serif",
          color: "#003149",
        }}
      >
        {planData.trial_info.trial_days_remaining} Days
      </Typography>
    </Box>

        <Box sx={{
        display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      textAlign: "center",
    }}>
      <Typography
        sx={{
          fontSize: "13px",
          fontFamily: "Poppins, sans-serif",
          color: "#333",
        }}
      >
     {t("subscriptionPage.trial_ends_on")}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          fontFamily: "Poppins, sans-serif",
          color: "#003149",
        }}
      >
        {new Date(planData.trial_info.trial_end_date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })}
      </Typography>
    </Box>
  </Box>
)}




{subscription_status?.status !== "trial" && (
        <Box
  sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mt: 0,
    pt: 1,
  }}
>
  <Typography
    sx={{
 fontSize: "13px", fontFamily: "Poppins, sans-serif", color: "#333"
    }}
  >
  {t("subscriptionPage.total")}
  </Typography>

  <Typography
    sx={{
      fontSize: "14px",
      fontFamily: "Poppins, sans-serif",
      fontWeight: 600,
      color: "#000",
    }}
  >



      {planData?.pricing_info?.current_pricing_breakdown?.total_amount
      ? `$${planData.pricing_info.current_pricing_breakdown.total_amount}`
      : current_plan?.amount
      ? `$${current_plan.amount.toFixed(2)}`
      : "0.00"}{" "}
    {/* /{current_plan?.billing_cycle || "monthly"} */}
    /{t(`billingCycle.${current_plan?.billing_cycle || "monthly"}`)}

  </Typography>
</Box>
)}

      </Box>

{!isFreePlan && subscription_status?.status !== "trial" && (
  <Box
    sx={{
      border: "1px solid #ddd",
      borderRadius: 2,
      p: 2,
      backgroundColor: "#fff",
    }}
  >
    <Typography
      sx={{
        fontSize: "14px",
        fontWeight: 600,
        fontFamily: "Poppins, sans-serif",
        mb: 1,
      }}
    >
       {t("subscriptionPage.next_renewal")}
    </Typography>

    <Typography
      sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}
    >
       {t("subscriptionPage.plan_renews_on")}{" "}
      {subscription_status?.next_due_date
        ? new Date(subscription_status.next_due_date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "N/A"}
    </Typography>
  </Box>
)}

    </Box>


<Modal open={openModal} onClose={() => setOpenModal(false)}>
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      bgcolor: "white",
      boxShadow: 24,
      borderRadius: 2,
      p: 3,
      width: 360,
      textAlign: "center",
    }}
  >
    {/* Header with title + close icon */}
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <CustomText sx={{ ...textStyles.h6, mb: 2, mt: 1, color: "#333" }}>
          {t("subscriptionPage.choose_worker_count")}
      </CustomText>
      <IconButton
        onClick={() => setOpenModal(false)}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "#666",
          "&:hover": { color: "#000" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>

    {/* Quantity Selector */}
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mb: 2 }}>
      <IconButton
        // onClick={() => setQuantity((p) => Math.max(1, p - 1))}
  onClick={() => {
    setQuantity((p) => {
      if (p <= initialQuantity) {
       toast.error(t("alerts.cannot_reduce_workers"));
        return p;
      }
      return p - 1;
    });
  }}
        size="small"
      sx={{
    backgroundColor: quantity <= initialQuantity ? "#e0e0e0" : "#f0f0f0",
    border: "1px solid #ccc",
    "&:hover": {
      backgroundColor: quantity <= initialQuantity ? "#e0e0e0" : "#e0e0e0",
    },
  }}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>

      <Box
        sx={{
          minWidth: 60,
          textAlign: "center",
          fontSize: "16px",
          fontWeight: 500,
          color: "#333",
          border: "1px solid #ccc",
          borderRadius: "6px",
          py: 0.5,
          px: 1,
        }}
      >
        {quantity}
      </Box>

      <IconButton
        onClick={() => setQuantity((p) => Math.min(1000, p + 1))}
        size="small"
        sx={{
          backgroundColor: "#f0f0f0",
          border: "1px solid #ccc",
          "&:hover": { backgroundColor: "#e0e0e0" },
        }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>

    {/* Pricing Summary */}
    {calculating && !pricingResult && (
      <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
        <CircularProgress size={24} />
      </Box>
    )}
    {pricingResult && (
      <Box
        sx={{
          backgroundColor: "#F9FAFB",
          borderRadius: 2,
          p: 2,
          mb: 2,
          textAlign: "left",
          border: "1px solid #ddd",
          opacity: calculating ? 0.6 : 1,
        }}
      >
        <CustomText
          text={pricingResult?.pricing?.plan_name || "Calculated Plan"}
          sx={{ ...textStyles.h6, color: "#333" }}
        />

        <Box
          sx={{
            fontSize: "13px",
            mt: 2,
            lineHeight: 1.8,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <span>{t("subscriptionPage.billing_cycle")}</span>
            <span>{pricingResult.billing_cycle}</span>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <span>{t("subscriptionPage.workers")}</span>
            <span>{pricingResult.pricing.worker_count}</span>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <span>{t("subscriptionPage.base")}</span>
            <span>{pricingResult.pricing.base_amount}</span>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <span>{t("subscriptionPage.additional_workers")}</span>
            <span>{pricingResult.pricing.additional_workers}</span>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <span>{t("subscriptionPage.additional_cost")}</span>
            <span>{pricingResult.pricing.additional_worker_cost}</span>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 1,
              fontWeight: "bold",
              borderTop: "1px solid #ddd",
              pt: 1,
            }}
          >
            <span>{t("subscriptionPage.total")}</span>
            <span>
              {pricingResult.pricing.total_amount} {pricingResult.currency}
            </span>
          </Box>
        </Box>
      </Box>
    )}

    {/* Action Button */}
    <Box sx={{ mt: 2 }}>
      <Button
        variant="contained"
        fullWidth
        disabled={!pricingResult || calculating || updating || quantity <= initialQuantity}
        sx={{
          backgroundColor: "#2E7D32",
          color: "#fff",
          textTransform: "none",
          "&:hover": { backgroundColor: "#256628" },
        }}
        onClick={handleContinue}
      >
        {updating ? (
          <CircularProgress size={22} sx={{ color: "#fff" }} />
        ) : calculating ? (
          <CircularProgress size={22} sx={{ color: "#fff" }} />
        ) : (
          t("subscriptionPage.continue")
        )}
      </Button>
    </Box>
  </Box>
</Modal>

<Modal open={showTopupModal} onClose={() => setShowTopupModal(false)}>
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      bgcolor: "white",
      boxShadow: 24,
      borderRadius: 2,
      p: 3,
      width: 380,
      textAlign: "left",
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <CustomText sx={{ ...textStyles.h6, mb: 2, mt: 1, color: "#333" }}>
        {t("subscriptionPage.confirm_topup_title")}
      </CustomText>
      <IconButton
        onClick={() => setShowTopupModal(false)}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "#666",
          "&:hover": { color: "#000" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>

    <Typography sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif", color: "#333", mb: 1.5 }}>
      {t("subscriptionPage.topup_employees_message", { count: topupQuote?.added_workers })}
    </Typography>
    <Typography sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif", color: "#333", mb: 1.5 }}>
      {t("subscriptionPage.topup_charge_message", {
        amount: `${topupQuote?.immediate_amount} ${topupQuote?.currency || ""}`.trim(),
      })}
    </Typography>
    <Typography sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif", color: "#333", mb: 1.5 }}>
      {t("subscriptionPage.topup_next_cycle_message")}
    </Typography>
    <Typography sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif", color: "#000", fontWeight: 600, mb: 2 }}>
      {t("subscriptionPage.topup_confirm_question")}
    </Typography>

    <Button
      variant="contained"
      fullWidth
      sx={{
        backgroundColor: "#2E7D32",
        color: "#fff",
        textTransform: "none",
        "&:hover": { backgroundColor: "#256628" },
      }}
      onClick={handleConfirmTopupPayment}
    >
      {t("subscriptionPage.confirm_and_pay")}
    </Button>
  </Box>
</Modal>

    </>
  );
};

export default CurrentPlans;


// import React, { useEffect, useState } from "react";
// import { Box, Typography, CircularProgress } from "@mui/material";
// import { useSelector } from "react-redux";
// import { decryptToken } from "../../../Utlis/Encryption.js";
// import { BASE_URL } from "../../../components/BaseUrl.js";
// import CustomText, { textStyles } from "../../../components/CustomText";
// import GroupIcon from "@mui/icons-material/Group";
// import EventIcon from "@mui/icons-material/Event";
// import CheckIcon from "@mui/icons-material/Check";
// import GlobalBannerAlert from "../../../components/GlobalPaymentAlert.js";
// import CustomButton from "../../../components/CustomButton.js";
// const CurrentPlans = () => {
//   const [planData, setPlanData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const token = useSelector((state) => state.auth.token);
//   const decryptedToken = token ? decryptToken(token) : null;
// const [showBanner, setShowBanner] = useState(true);

//   useEffect(() => {
//     const fetchPlan = async () => {
//       try {
//         const response = await fetch(
//           `${BASE_URL}/payments/company-admin/plan-details`,
//           {
//             method: "GET",
//             headers: {
//               "Content-Type": "application/json",
//               Authorization: `Bearer ${decryptedToken}`,
//             },
//           }
//         );

//         if (!response.ok) {
//           throw new Error(`HTTP error! status: ${response.status}`);
//         }

//         const result = await response.json();
//         setPlanData(result.data);
//       } catch (error) {
//         console.error("Error fetching plan details:", error);
//         setPlanData(null);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchPlan();
//   }, []);

//   if (loading) {
//     return (
//       <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
//         <CircularProgress size={24} />
//       </Box>
//     );
//   }

//   if (!planData) {
//     return (
//       <Typography
//         sx={{
//           fontSize: "13px",
//           fontFamily: "Poppins, sans-serif",
//           color: "red",
//         }}
//       >
//         Failed to load plan details.
//       </Typography>
//     );
//   }
// const { current_plan, subscription_status } = planData;
// const isFreePlan = current_plan?.plan_id === "free" || current_plan?.key === "free";
//   return (
//     <>
//     <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
//         <Box sx={{
//           display: "flex", justifyContent: "space-between", alignItems: "center"
//         }}>


//       <CustomText
//         sx={{
//           ...textStyles.h1,
//           fontWeight: 500,
//           fontSize: "16px",
//           color: "#003149",
//           textAlign: "left",
//         }}
//       >
//         Current Subscription Plan
//       </CustomText>
//       <CustomButton label="Add Workers"
//       size='small'
//       />
//         </Box>
//       {/* Current Plan Box */}
//       <Box
//         sx={{
//           border: "1px solid #ddd",
//           borderRadius: 2,
//           p: 2,
//         }}
//       >
     

//           {/* Plan name with status */}
// <Box
//   sx={{
//     display: "flex",
//     justifyContent: "space-between",
//     alignItems: "center",
//   }}
// >
//   <CustomText
//     sx={{
//       ...textStyles.h1,
//       fontWeight: 600,
//       fontSize: "18px",
//       color: "#003149",
//     }}
//   >
//     {current_plan?.name || "No Plan Name"}
//   </CustomText>


// {!isFreePlan && planData?.subscription_status && (
//   <Box
//     sx={{
//       backgroundColor:
//         planData.subscription_status.status === "trial"
//           ? "#f0ad4e"
//           : "#5cb85c",
//       color: "#fff",
//       fontSize: "12px",
//       fontFamily: "Poppins, sans-serif",
//       fontWeight: 500,
//       px: 1.5,
//       py: 0.4,
//       borderRadius: "4px",
//       textTransform: "capitalize",
//     }}
//   >
//     {planData.subscription_status.status === "trial"
//       ? "Trial"
//       : planData.subscription_status.detailed_status?.replace("_", " ")}
//   </Box>
// )}

// </Box>




//         {planData?.pricing_info?.current_pricing_breakdown && (
//   <Box
//     sx={{
//       border: "1px solid #ddd",
//       borderRadius: 2,
//       p: 2,
//       mt: 2,
//       // backgroundColor: "#fafafa",
//     }}
//   >    <Typography
//       sx={{
//         fontSize: "14px",
//         fontWeight: 600,
//         fontFamily: "Poppins, sans-serif",
//         mb: 1,
//         color: "#003149",
//       }}
//     >
//       Pricing Details
//     </Typography>
//     {[
//       { label: "Worker Count", value: planData.pricing_info.current_pricing_breakdown.worker_count },
//       { label: "Base Amount", value: `$${planData.pricing_info.current_pricing_breakdown.base_amount}` },
//       { label: "Per Worker Amount", value: `$${planData.pricing_info.current_pricing_breakdown.per_worker_amount}` },
//       { label: "Additional Worker Cost", value: `$${planData.pricing_info.current_pricing_breakdown.additional_worker_cost}` },
//       { label: "Total Amount", value: `$${planData.pricing_info.current_pricing_breakdown.total_amount}` },
//     ].map((item, index) => (
//       <Box
//         key={index}
//         sx={{
//           display: "flex",
//           justifyContent: "space-between",
//           alignItems: "center",
//           py: 0.6,
//           borderBottom: index !== 4 ? "1px dashed #eee" : "none",
//         }}
//       >
//         <Typography
//           sx={{
//             fontSize: "13px",
//             fontFamily: "Poppins, sans-serif",
//             color: "#333",
//           }}
//         >
//           {item.label}
//         </Typography>
//         <Typography
//           sx={{
//             fontSize: "14px",
//             fontWeight: 600,
//             fontFamily: "Poppins, sans-serif",
//             color: "#000",
//           }}
//         >
//           {item.value}
//         </Typography>
//       </Box>
//     ))}
//   </Box>
// )}

// {subscription_status?.status === "trial" && planData?.trial_info && (
//   <Box
//     sx={{
//       // border: "1px solid #ddd",
//       borderRadius: 2,
//       // p: 2,
//       mt:1,
//       // backgroundColor: "#fafafa",
    
//     }}
//   >
//     <Box sx={{
//         display: "flex",
//       justifyContent: "space-between",
//       alignItems: "center",
//       textAlign: "center",
//     }}>
//       <Typography
//         sx={{
//           fontSize: "13px",
//           fontFamily: "Poppins, sans-serif",
//           color: "#333",
//         }}
//       >
//         Trial Days Remaining
//       </Typography>
//       <Typography
//         sx={{
//           fontSize: "14px",
//           fontWeight: 600,
//           fontFamily: "Poppins, sans-serif",
//           color: "#003149",
//         }}
//       >
//         {planData.trial_info.trial_days_remaining} Days
//       </Typography>
//     </Box>

//         <Box sx={{
//         display: "flex",
//       justifyContent: "space-between",
//       alignItems: "center",
//       textAlign: "center",
//     }}>
//       <Typography
//         sx={{
//           fontSize: "13px",
//           fontFamily: "Poppins, sans-serif",
//           color: "#333",
//         }}
//       >
//         Trial Ends On
//       </Typography>
//       <Typography
//         sx={{
//           fontSize: "14px",
//           fontWeight: 600,
//           fontFamily: "Poppins, sans-serif",
//           color: "#003149",
//         }}
//       >
//         {new Date(planData.trial_info.trial_end_date).toLocaleDateString("en-US", {
//           year: "numeric",
//           month: "long",
//           day: "numeric",
//         })}
//       </Typography>
//     </Box>
//   </Box>
// )}




// {subscription_status?.status !== "trial" && (
//         <Box
//   sx={{
//     display: "flex",
//     justifyContent: "space-between",
//     alignItems: "center",
//     mt: 0,
//     pt: 1,
//   }}
// >
//   <Typography
//     sx={{
//  fontSize: "13px", fontFamily: "Poppins, sans-serif", color: "#333"
//     }}
//   >
//     Total
//   </Typography>

//   <Typography
//     sx={{
//       fontSize: "14px",
//       fontFamily: "Poppins, sans-serif",
//       fontWeight: 600,
//       color: "#000",
//     }}
//   >



//       {planData?.pricing_info?.current_pricing_breakdown?.total_amount
//       ? `$${planData.pricing_info.current_pricing_breakdown.total_amount}`
//       : current_plan?.amount
//       ? `$${current_plan.amount.toFixed(2)}`
//       : "0.00"}{" "}
//     /{current_plan?.billing_cycle || "monthly"}

//   </Typography>
// </Box>
// )}

//       </Box>

// {!isFreePlan && subscription_status?.status !== "trial" && (
//   <Box
//     sx={{
//       border: "1px solid #ddd",
//       borderRadius: 2,
//       p: 2,
//       backgroundColor: "#fff",
//     }}
//   >
//     <Typography
//       sx={{
//         fontSize: "14px",
//         fontWeight: 600,
//         fontFamily: "Poppins, sans-serif",
//         mb: 1,
//       }}
//     >
//       Next Renewal
//     </Typography>

//     <Typography
//       sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}
//     >
//       Your Plan Renews on{" "}
//       {subscription_status?.next_due_date
//         ? new Date(subscription_status.next_due_date).toLocaleDateString("en-US", {
//             year: "numeric",
//             month: "long",
//             day: "numeric",
//           })
//         : "N/A"}
//     </Typography>
//   </Box>
// )}

//     </Box>
//     </>
//   );
// };

// export default CurrentPlans;




